import * as _ from "lodash"
import {
    IAutomationOperationIfParameters,
    IAutomationOperationSequenceParameters,
    IAutomationOperationWhileParameters,
    IFlow
} from './PA.FlowControl.IF'

import {IDomainContext} from "./PA.Domain.IF";


export class FlowEditorUtils {
    static createEmptyFlow(): IFlow {
        let newFlow: IFlow = {
            type: 'Flow',
            operation: {},
            condition: {},
            domainContext: {}
        }
        // First Operation gets operation Id = 1
        FlowEditorUtils.addSequenceOperation(newFlow, 'Main Sequence')
        return newFlow
    }

    static addIfOperation(flow: IFlow, sName?: string): number {
        let newOpId = FlowEditorUtils.addEmptyOperation(flow)
        let op = flow.operation[newOpId]
        op.def.type = 'If'
        let params: IAutomationOperationIfParameters = {
            name: sName || '',
            condition: 0,
            trueOperation: 0,
            falseOperation: 0
        }
        params.trueOperation = FlowEditorUtils.addSequenceOperation(flow, 'If True')
        params.falseOperation = FlowEditorUtils.addSequenceOperation(flow, 'Else')
        op.param = params
        return newOpId
    }

    static addWhileOperation(flow: IFlow): number {
        let newOpId = FlowEditorUtils.addEmptyOperation(flow)
        let op = flow.operation[newOpId]
        op.def.type = 'While'
        let params: IAutomationOperationWhileParameters = { name: '', condition: 0, iterationOperation: 0 }
        params.iterationOperation = FlowEditorUtils.addSequenceOperation(flow, 'While')
        op.param = params
        return newOpId
    }

    static addSequenceOperation(flow: IFlow, sName?: string): number {
        let newOpId = FlowEditorUtils.addEmptyOperation(flow)
        let op = flow.operation[newOpId]
        op.def.type = 'Sequence'
        op.param = <IAutomationOperationSequenceParameters>{ name: sName || 'Sequence', steps: [] }
        return newOpId
    }

    static addExitFlowOperation(flow: IFlow): number {
        let newOpId = FlowEditorUtils.addEmptyOperation(flow)
        let op = flow.operation[newOpId]
        op.def.type = 'ExitFlow'
        op.param = { name: 'Exit Flow' }
        return newOpId
    }

    static addDomainOperation(flow: IFlow, context: IDomainContext, operationId: string): number {
        let dOp = _.first(_.filter(context.operations, (o) => o.id === operationId))
        if (!dOp) return 0

        let newOpId = FlowEditorUtils.addEmptyOperation(flow)
        let op = flow.operation[newOpId]
        let sName = dOp.name
        op.def.type = 'DomainOperation'
        op.def.domainId = context.id
        op.def.domainOperationId = operationId
        op.param = _.defaults(_.clone(dOp.defaultParameters || {}), { name: sName })
        return newOpId
    }

    static getOperationParentDictionary(flow: IFlow): { [childOperationId: number]: number } {
        let rv: { [childOperationId: number]: number } = {}

        // Recursively add parent
        function addParents(parentId: number, childId: number) {
            if (+parentId > 0 && +childId > 0) rv[childId] = +parentId

            let op = flow?.operation?.[childId]
            switch (op?.def?.type) {
                case 'If': {
                    let params = <IAutomationOperationIfParameters>op.param
                    addParents(+childId, params?.trueOperation)
                    addParents(+childId, params?.falseOperation)
                    break
                }

                case 'While': {
                    let params = <IAutomationOperationWhileParameters>op.param
                    addParents(+childId, params?.iterationOperation)
                    break
                }

                case 'Sequence': {
                    let params = <IAutomationOperationSequenceParameters>op.param
                    _.forEach(params?.steps || [], (seqChildId) => {
                        addParents(+childId, seqChildId)
                    })
                    break
                }

                case 'ExitFlow':
                case 'DomainOperation':
                    break
            }
        }
        // Start at the Main Sequence, Recurse
        addParents(0, 1)

        return rv
    }

    static removeOperation(flow: IFlow, operationId: number, skipOpPurge?: boolean): void {
        // Recursively remove operation
        function removeFrom(fromId: number) {
            let op = flow?.operation?.[fromId]
            if (_.isEmpty(op)) return

            switch (op?.def?.type) {
                case 'If': {
                    let params = <IAutomationOperationIfParameters>op.param
                    removeFrom(+params?.trueOperation)
                    removeFrom(+params?.falseOperation)
                    if (params?.trueOperation == operationId) {
                        params.trueOperation = 0
                    }
                    if (params?.falseOperation == operationId) {
                        params.falseOperation = 0
                    }
                    break
                }

                case 'While': {
                    let params = <IAutomationOperationWhileParameters>op.param
                    removeFrom(+params?.iterationOperation)
                    if (params?.iterationOperation == operationId) {
                        params.iterationOperation = 0
                    }
                    break
                }

                case 'Sequence': {
                    let params = <IAutomationOperationSequenceParameters>op.param
                    _.forEach(params?.steps || [], (seqChildId) => {
                        removeFrom(+seqChildId)
                    })
                    params.steps = _.filter(params?.steps || [], (a) => a != operationId)
                    break
                }

                case 'ExitFlow':
                case 'DomainOperation':
                    break
            }
        }
        // Start at the Main Sequence, Recurse
        removeFrom(1)

        // This should also delete children operations
        if (!skipOpPurge) {
            if (operationId in flow.operation) {
                delete flow.operation[operationId]
            }
        }
    }

    static addDomainContext(flow: IFlow, domainId: string, version?: string): void {
        if (_.isEmpty(flow)) return
        if (_.isEmpty(domainId)) return

        flow.domainContext ||= {}
        flow.domainContext[domainId] = {
            id: domainId,
            version: version || ''
        }
    }

    static removeDomainContext(flow: IFlow, domainId: string): void {
        if (_.isEmpty(flow)) return
        if (_.isEmpty(domainId)) return

        flow.domainContext ||= {}
        if (domainId in flow.domainContext) {
            delete flow.domainContext[domainId]
        }
    }

    static NamedCondition_updateOrCreate(flow: IFlow, domainId: string, namedConditionId: string, existingConditionId?: number): number {
        if (_.isEmpty(flow)) return 0
        let cndItem
        if((existingConditionId > 0) && (existingConditionId in flow.condition) ){
            cndItem = flow.condition[existingConditionId]
        } else {
            let cndId = FlowEditorUtils.addEmptyCondition(flow)
            cndItem = flow.condition[cndId]
        }
        cndItem.def.type = "NamedCondition"
        cndItem.def.domainId = domainId
        cndItem.def.namedConditionId = namedConditionId
        return + cndItem.def.conditionId

    }

    static Sequence_moveOperationNext(flow: IFlow, seqOperationId: number, moveOperationId: number) {
        if (_.isEmpty(flow?.operation)) return

        if (!(+moveOperationId > 0)) return

        let seqOp = flow.operation?.[seqOperationId]
        if (_.isEmpty(seqOp)) return
        if (seqOp?.def?.type !== 'Sequence') return

        let params = <IAutomationOperationSequenceParameters>seqOp.param
        let idxOfOp = params.steps.indexOf(+moveOperationId)
        if (idxOfOp === -1) return
        if (idxOfOp == params.steps.length - 1) return
        let saveOpId = params.steps[idxOfOp + 1]
        params.steps[idxOfOp + 1] = +moveOperationId
        params.steps[idxOfOp] = saveOpId
    }

    static Sequence_moveOperationPrevious(flow: IFlow, seqOperationId: number, moveOperationId: number) {
        if (_.isEmpty(flow?.operation)) return

        if (!(+moveOperationId > 0)) return

        let seqOp = flow.operation?.[seqOperationId]
        if (_.isEmpty(seqOp)) return
        if (seqOp?.def?.type !== 'Sequence') return

        let params = <IAutomationOperationSequenceParameters>seqOp.param
        let idxOfOp = params.steps.indexOf(+moveOperationId)
        if (idxOfOp === -1) return
        if (idxOfOp === 0) return

        let saveOpId = params.steps[idxOfOp - 1]
        params.steps[idxOfOp - 1] = +moveOperationId
        params.steps[idxOfOp] = saveOpId
    }

    static getParentSequence(flow: IFlow, dctPrnt: { [childOperationId: number]: number }, opId: number): number {
        if (!(opId in dctPrnt)) return 0
        let parentOpId = +dctPrnt[opId]
        let op = flow.operation[parentOpId]
        if (op.def?.type === 'Sequence') return parentOpId
        return FlowEditorUtils.getParentSequence(flow, dctPrnt, parentOpId)
    }

    private static addEmptyOperation(flow: IFlow): number {
        let newOpId = FlowEditorUtils.lastOperationId(flow) + 1

        flow.operation[newOpId] = {
            // @ts-expect-error "No Type Defined"
            def: {operationId: newOpId, type: undefined },
            param: { name: '' }
        }
        return newOpId
    }

    private static lastOperationId(flow: IFlow): number {
        // Look at operation dictionary to find last operation id
        let lastId = 0

        _.forEach(flow?.operation || {}, (op, idx) => {
            lastId = Math.max(lastId, +idx)
        })

        return lastId
    }

    private static addEmptyCondition(flow: IFlow): number {
        let newCndId = FlowEditorUtils.lastConditionId(flow) + 1

        flow.condition[newCndId] = {
            // @ts-expect-error "No Type Defined"
            def: { conditionId: newCndId, type: undefined },
            param: { name: '' }
        }
        return newCndId
    }

    private static lastConditionId(flow: IFlow): number {
        // Look at operation dictionary to find last operation id
        let lastId = 0

        _.forEach(flow?.condition || {}, (op, idx) => {
            lastId = Math.max(lastId, +idx)
        })

        return lastId
    }
}
